// Hideo - profile files: "Save" (floppy disk) and "Load" in the Profiles window, and loading a file from an online service.
// Ported from the legacy core/data.js (verbatim copy in tests/fixtures/legacy/data.js, golden tests in tests/saving*.test.ts).
import { t, tv } from '../../i18n'
import { normalizeData } from '../../data/normalize'
import type { ProfileData } from '../../data/types'
import { useDataStore } from '../../stores/data'
import { usePickerStore } from '../../stores/picker'
import { useProjectsStore } from '../../stores/projects'
import { uiAlert, uiConfirm } from '../../stores/notices'
import { fileStamp, fmtWhen, setSync, syncOf, timeParts } from './sync'
import { profileFileText, writeProfileData } from './save'
import { mediaExtract } from '../cloud/media'
import { isDesktop, saveTextAs } from '../desktop'
import { slugify } from '../seo'

/** The data is a Hideo profile file */
export const validProfileFile = (d: unknown): boolean => !!d && Array.isArray((d as ProfileData).videos) && Array.isArray((d as ProfileData).creators)

/** The browser's "Save as" window (Chromium) */
type SavePicker = (o: { suggestedName: string; types: { description: string; accept: Record<string, string[]> }[] }) => Promise<FileSystemFileHandle>

/** Saves a profile (any, active or not) with all its data in a .json file: the desktop app and Chromium ask where, other
 *  browsers download */
export async function saveJson(id: string = useDataStore().profiles.current): Promise<void> {
  const p = useDataStore().profiles.list.find(x => x.id === id); if (!p) return
  const slug = slugify(p.name) || t('profile')
  // Local date and time in the name, so the most recent file is easy to spot on Drive
  const stamp = fileStamp(), n = timeParts()
  const name = `hideo-${slug}-${n.y}-${n.mo}-${n.d}-${n.h}${n.mi}.json`
  const text = () => profileFileText(id, stamp, true)
  const done = () => { setSync(id, { dirty: false, file: { ...stamp, kind: 'salvato' } }) }
  if (isDesktop()) { if (await saveTextAs(name, text)) done(); return }
  const picker = (window as unknown as { showSaveFilePicker?: SavePicker }).showSaveFilePicker
  if (picker) {
    // The picker must open at once (it needs the user's click); the data is read afterwards
    try { const fh = await (window as unknown as { showSaveFilePicker: SavePicker }).showSaveFilePicker({ suggestedName: name, types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }] }); const w = await fh.createWritable(); await w.write(await text()); await w.close(); done() }
    catch (e) { if ((e as Error).name !== 'AbortError') { console.error(e); uiAlert(t('Saving the file failed.')) } }
    return
  }
  // Phone / browser without a picker: download (on the phone it then goes to Google Drive)
  const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([await text()], { type: 'application/json' })); a.download = name
  document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 10000)
  done()
}

let importTarget: string | null = null
/** Chooses a .json file to load into a profile (the hidden #importJsonFile field of index.html) */
export function importInto(id: string): void { importTarget = id; (document.getElementById('importJsonFile') as HTMLInputElement).click() }
/** The file chosen in #importJsonFile is loaded (installed once on the document, at load time: loadApp, src/features/boot.ts) */
export function installImport(doc: Pick<Document, 'addEventListener'> = document): void {
  doc.addEventListener('change', async e => {
    const el = e.target as HTMLInputElement
    if (el.id !== 'importJsonFile' || !(el.files as FileList)[0]) return
    const PROFILES = useDataStore().profiles
    const id = importTarget || PROFILES.current
    importTarget = null
    let d = null
    try { d = JSON.parse(await (el.files as FileList)[0].text()); if (!validProfileFile(d)) throw 0 }
    catch { uiAlert(t('Invalid file.')); el.value = ''; return }
    el.value = ''
    await applyLoadedFile(id, d, 'caricato', 'file')
  })
}

/** Loads a profile file (from the computer or from an online service) after confirmation; returns true if it was loaded.
 *  Sync protection: warns before losing changes or going back to an older file.
 *  from = where the data comes from: 'file', the name of an online service (e.g. 'Google Drive') or '' (unknown).
 *  silent = automatic load of the most recent online version (autosync.ts), without confirmation */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function applyLoadedFile(id: string, d: any, kind: string, from?: string, silent = false): Promise<boolean> {
  const PROFILES = useDataStore().profiles
  const p = PROFILES.list.find(x => x.id === id); if (!p) return false
  const s = syncOf(id), fileAt = typeof d.savedAt === 'string' && !isNaN(new Date(d.savedAt) as unknown as number) ? d.savedAt : ''
  const fileOn = d.savedOn === 'telefono' || d.savedOn === 'computer' ? d.savedOn : ''
  const warn: string[] = []
  if (s.dirty) warn.push(t('⚠ This profile has unsaved changes: they will be lost if you load.'))
  if (fileAt && s.file && s.file.savedAt && fileAt < s.file.savedAt) warn.push(t('⚠ The file is OLDER ({date}) than the last file used here ({last}).', { date: fmtWhen(fileAt), last: fmtWhen(s.file.savedAt) }))
  const info = !fileAt ? t("The file doesn't contain the save date (old format).")
    : fileOn ? t('Saved on {date} from the {device}.', { date: fmtWhen(fileAt), device: tv(fileOn) }) : t('Saved on {date}.', { date: fmtWhen(fileAt) })
  const src = String(from || '').trim(), service = src && src !== 'file' ? src : ''
  const question = service ? t('Replace all the data of the profile "{name}" with the data saved on {service}?', { name: p.name, service })
    : src ? t('Replace all the data of the profile "{name}" with the data from the file?', { name: p.name })
    : t('Replace all the data of the profile "{name}" with these?', { name: p.name })
  if (!silent && !(await uiConfirm(`${question}\n\n${info}` + (warn.length ? '\n\n' + warn.join('\n') : '')
    + (d.profile ? '\n\n' + t('The file also contains the photo, bio and socials of the profile: they will be applied.') : ''), { ok: t('Load'), title: t('Load this data?'), icon: 'fa-cloud-arrow-down' }))) return false
  const { profile, ...data } = d
  try {
    await mediaExtract(data)   // Base64 images and voice notes (floppy-disk or old files): out of the browser data
    await writeProfileData(id, normalizeData(data))
    if (profile && typeof profile === 'object') usePickerStore().applyInfo(p, profile)
    setSync(id, { dirty: false, file: { savedAt: fileAt, savedOn: fileOn, kind } })
    if (id === PROFILES.current) void useProjectsStore().ensureFirst()   // file from before projects: the first one is created
    return true
  } catch (err) { console.error(err); uiAlert(t('Loading failed: is the browser storage full?')); return false }
}
